const customRoot = document.querySelector('[data-custom-product]');

if (customRoot && !customRoot.dataset.customReady) {
  customRoot.dataset.customReady = 'true';

  const query = (selector, scope = customRoot) => scope.querySelector(selector);
  const queryAll = (selector, scope = customRoot) => [...scope.querySelectorAll(selector)];
  const product = JSON.parse(query('[data-custom-product-json]').textContent);
  const variants = product.variants || [];
  const form = query('[data-custom-form]');
  const masterVariant = query('[data-custom-variant]');
  const optionInputs = queryAll('[data-custom-option]');
  const optionGroups = queryAll('[data-custom-option-group]');
  const addonInputs = queryAll('[data-custom-addon]');
  const drillGroup = optionGroups.find((group) => group.dataset.optionKind === 'drill');
  const drillIndex = drillGroup ? Number(drillGroup.dataset.optionIndex) : -1;
  const enhancementGroup = optionGroups.find((group) => group.dataset.optionKind === 'enhancement');
  const enhancementIndex = enhancementGroup ? Number(enhancementGroup.dataset.optionIndex) : -1;
  const submit = query('[data-custom-submit]');
  const submitLabel = query('[data-custom-submit-label]');
  const submitPrice = query('[data-custom-submit-price]');
  const submitNote = query('[data-custom-submit-note]');
  const formStatus = query('[data-custom-status]');
  const price = query('[data-custom-price]');
  const comparePrice = query('[data-custom-compare]');
  const total = query('[data-custom-total]');
  const quantity = query('[name="quantity"]', form);
  const photoUrlInput = query('[data-photo-url]');
  const kitInstanceInput = query('[data-custom-instance]');
  const optionsGuidance = query('[data-options-guidance]');
  const success = query('[data-custom-success]');
  const successMessage = query('[data-custom-success-message]');
  const DRILLS_PER_CM = 4;
  const SMALL_PAINTING_MAX_COLOURS = 45;
  const LARGE_PAINTING_MAX_COLOURS = 60;

  const fileInput = query('[data-photo-input]');
  const dropzone = query('[data-upload-dropzone]');
  const uploadStatus = query('[data-upload-status]');
  const retry = query('[data-retry-upload]');
  const editor = query('[data-preview-editor]');
  const canvas = query('[data-preview-canvas]');
  const context = canvas.getContext('2d');
  const analysis = query('[data-image-analysis]');
  const zoomControl = query('[data-preview-zoom]');
  const brightnessControl = query('[data-preview-brightness]');
  const previewModeButtons = queryAll('[data-preview-mode]');

  let selectedImage = null;
  let uploadedUrl = '';
  let uploadController = null;
  let uploadInProgress = false;
  let productionExportTimer = 0;
  let productionExportRevision = 0;
  let pendingProductionSignature = '';
  let uploadedProductionSignature = '';
  let previewMode = 'diamond';
  let zoom = 1;
  let rotation = 0;
  let brightness = 100;
  let panX = 0;
  let panY = 0;
  let dragging = false;
  let dragStart = { x: 0, y: 0, panX: 0, panY: 0 };
  let editKey = '';
  let existingPhotoUrl = '';
  let originalVariantId = '';
  let replacementAdded = false;
  let userSelectedSize = false;
  let showAllSizes = false;
  let sizePicker = null;
  let previewFrame = 0;
  const STATE = Object.freeze({
    WAITING: 'waiting',
    EDITING: 'editing',
    OPTIONS: 'options',
    READY: 'ready',
    BUSY: 'busy',
    RESULT: 'result'
  });
  let builderState = STATE.WAITING;
  const parameters = new URLSearchParams(window.location.search);
  const editPhoto = parameters.get('dp_photo') || '';
  const editLine = parameters.get('dp_line') || parameters.get('dp_key') || '';
  const isEditSession = parameters.get('dp_edit') === '1' && Boolean(editPhoto && editLine);

  // Keep one server-rendered copy of each section while presenting the shorter
  // mobile journey: builder, customer proof, then practical guidance.
  const mobileLayout = window.matchMedia('(max-width: 749px)');
  const builderSection = query('#CustomBuilder');
  const tipsSection = query('[data-custom-tips]');
  const galleryElement = document.querySelector('[data-custom-gallery]');
  const gallerySection = galleryElement?.closest('.shopify-section') || galleryElement;
  const galleryMarker = gallerySection ? document.createComment('custom-gallery-home') : null;
  const tipsMarker = tipsSection ? document.createComment('custom-tips-home') : null;

  if (gallerySection && galleryMarker) gallerySection.before(galleryMarker);
  if (tipsSection && tipsMarker) tipsSection.before(tipsMarker);

  function arrangeMobileContent(event = mobileLayout) {
    if (event.matches) {
      let anchor = builderSection;
      if (gallerySection && anchor) {
        anchor.after(gallerySection);
        anchor = gallerySection;
      }
      if (tipsSection && anchor) anchor.after(tipsSection);
      return;
    }
    if (gallerySection && galleryMarker) galleryMarker.after(gallerySection);
    if (tipsSection && tipsMarker) tipsMarker.after(tipsSection);
  }

  arrangeMobileContent();
  mobileLayout.addEventListener('change', arrangeMobileContent);

  const galleryCta = galleryElement?.querySelector('[data-custom-gallery-cta]');
  galleryCta?.addEventListener('click', (event) => {
    if (!mobileLayout.matches || !builderSection) return;
    event.preventDefault();
    builderSection.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start'
    });
    (selectedImage ? canvas : fileInput).focus({ preventScroll: true });
    window.history.replaceState(null, '', '#CustomBuilder');
  });

  function optionsAreAvailable() {
    return Boolean(selectedImage && ![STATE.WAITING, STATE.EDITING].includes(builderState));
  }

  function setBuilderState(nextState) {
    const wasUnlocked = optionsAreAvailable();
    builderState = nextState;
    customRoot.dataset.customState = nextState;
    const unlocked = optionsAreAvailable();
    if (!unlocked) {
      optionInputs.forEach((input) => { input.disabled = true; });
      addonInputs.forEach((input) => { input.disabled = true; });
    } else if (!wasUnlocked) {
      optionInputs.forEach((input) => { input.disabled = false; });
      addonInputs.forEach((input) => { input.disabled = false; });
    }
    if (quantity) quantity.disabled = !unlocked;
    if (total && !unlocked) total.hidden = true;
    if (nextState === STATE.WAITING) {
      optionInputs.filter((input) => Number(input.dataset.optionIndex) !== drillIndex).forEach((input) => { input.checked = false; });
      const drillInputs = optionInputs.filter((input) => Number(input.dataset.optionIndex) === drillIndex);
      if (!drillInputs.some((input) => input.checked)) {
        const defaultDrill = drillInputs.find((input) => /^round$/i.test(input.value)) || drillInputs[0];
        if (defaultDrill) defaultDrill.checked = true;
      }
      if (comparePrice) comparePrice.hidden = true;
      if (price) price.textContent = '';
      submitPrice.hidden = true;
      submitLabel.textContent = 'Add to cart';
      submitNote.textContent = 'Upload your photo before adding to cart.';
    }
    refreshSizePicker();
  }

  function formatMoney(cents) {
    if (window.PDCart?.money) return window.PDCart.money(cents, product.currency);
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: product.currency
    }).format(Number(cents || 0) / 100);
  }

  function formatAddonMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: product.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(Number(cents || 0) / 100);
  }

  function normalizeOption(value) {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  function enhancementFlags(value) {
    const normalized = normalizeOption(value);
    return {
      ab: /(^|\s|\+)ab(?:\s|$)|aurora borealis/.test(normalized),
      priority: /priority/.test(normalized)
    };
  }

  function addonFlags() {
    return {
      ab: Boolean(addonInputs.find((input) => input.dataset.customAddon === 'ab')?.checked),
      priority: Boolean(addonInputs.find((input) => input.dataset.customAddon === 'priority')?.checked)
    };
  }

  function enhancementValueFor(flags) {
    if (!enhancementGroup) return null;
    return queryAll('[data-custom-option]', enhancementGroup).find((input) => {
      const candidate = enhancementFlags(input.value);
      return candidate.ab === flags.ab && candidate.priority === flags.priority;
    })?.value || null;
  }

  function selectedValues(flags = addonFlags()) {
    const values = [];
    optionInputs.filter((input) => input.checked && Number(input.dataset.optionIndex) !== enhancementIndex).forEach((input) => {
      values[Number(input.dataset.optionIndex)] = input.value;
    });
    if (enhancementIndex >= 0) values[enhancementIndex] = enhancementValueFor(flags);
    return values;
  }

  function findVariant(values) {
    return variants.find((variant) => variant.options.every((value, index) => value === values[index]));
  }

  function currentVariant() {
    return findVariant(selectedValues()) || null;
  }

  function isValueAvailable(optionIndex, value, selections) {
    return variants.some((variant) => variant.available && variant.options[optionIndex] === value && variant.options.every((optionValue, index) => index === optionIndex || optionValue === selections[index]));
  }

  function updateOptionAvailability(selections) {
    optionInputs.filter((input) => Number(input.dataset.optionIndex) !== enhancementIndex).forEach((input) => {
      const index = Number(input.dataset.optionIndex);
      const available = isValueAvailable(index, input.value, selections);
      input.disabled = !available && !input.checked;
      input.nextElementSibling?.classList.toggle('is-unavailable', input.disabled);
    });
  }

  function variantForAddonFlags(flags) {
    return findVariant(selectedValues(flags)) || null;
  }

  function updateAddonControls() {
    const unlocked = optionsAreAvailable();
    const standard = variantForAddonFlags({ ab: false, priority: false });
    addonInputs.forEach((input) => {
      const type = input.dataset.customAddon;
      const flags = { ab: false, priority: false, [type]: true };
      const addonVariant = variantForAddonFlags(flags);
      const output = query(`[data-custom-addon-price="${type}"]`);
      const difference = Number(addonVariant?.price || 0) - Number(standard?.price || 0);
      if (output) output.textContent = difference > 0 ? `(+${formatAddonMoney(difference)})` : '';

      const nextFlags = addonFlags();
      nextFlags[type] = !input.checked;
      const nextVariant = variantForAddonFlags(nextFlags);
      input.disabled = !unlocked || (!input.checked && !nextVariant?.available);
      input.closest('.pd-custom-addon-card')?.classList.toggle('is-selected', input.checked);
    });
  }

  function prepareOptionLayout() {
    const purchase = query('.pd-custom-purchase', form);
    const extras = query('[data-custom-extras]', form);
    const anchor = extras || purchase;
    const priority = { size: 0, drill: 1, enhancement: 2, generic: 3 };
    [...optionGroups]
      .sort((first, second) => (priority[first.dataset.optionKind] ?? 3) - (priority[second.dataset.optionKind] ?? 3))
      .forEach((group) => form.insertBefore(group, anchor));
  }

  function parseSize(value) {
    const dimensions = String(value || '').match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
    if (!dimensions) return null;
    const width = Number(dimensions[1]);
    const height = Number(dimensions[2]);
    return {
      width,
      height,
      key: `${Math.min(width, height)}x${Math.max(width, height)}`,
      ratio: Math.max(width, height) / Math.min(width, height),
      area: width * height,
      label: String(value)
    };
  }

  function sourceDimensions() {
    if (!selectedImage) return null;
    const rotated = Math.abs(rotation % 180) === 90;
    return {
      width: rotated ? selectedImage.naturalHeight : selectedImage.naturalWidth,
      height: rotated ? selectedImage.naturalWidth : selectedImage.naturalHeight
    };
  }

  function ratioDifference(first, second) {
    return Math.abs(Math.log(first / second));
  }

  function preferredSizeRecommendation() {
    const source = sourceDimensions();
    if (!source?.width || !source?.height) return null;
    const ratio = Math.max(source.width / source.height, source.height / source.width);
    if (ratio <= 1.15) return { keys: ['40x40'], minimumArea: 1600 };
    if (ratio < 1.35) return { keys: ['40x50', '45x60'], minimumArea: 2000 };
    if (ratio < 1.5) return { keys: ['45x60'], minimumArea: 2700 };
    if (ratio < 1.85) return { keys: ['45x75', '60x100'], minimumArea: 3375 };
    if (ratio <= 2.25) return { keys: ['40x80', '50x100'], minimumArea: 3200 };
    return { keys: [], minimumArea: 0 };
  }

  function matchingSizeInput(sizeKey, currentSelections = selectedValues()) {
    const sizeGroup = optionGroups.find((group) => group.dataset.optionKind === 'size');
    if (!sizeGroup) return null;
    const sizeIndex = Number(sizeGroup.dataset.optionIndex);
    const candidates = optionInputs.filter((input) => Number(input.dataset.optionIndex) === sizeIndex && parseSize(input.value)?.key === sizeKey);
    return candidates.find((input) => {
      const selections = [...currentSelections];
      selections[sizeIndex] = input.value;
      return Boolean(findVariant(selections)?.available);
    }) || null;
  }

  function applyVariantSelection(variant, restoreAddonState = false) {
    if (!variant) return false;
    variant.options.forEach((value, index) => {
      const input = optionInputs.find((option) => Number(option.dataset.optionIndex) === index && option.value === value);
      if (input) input.checked = true;
    });
    if (restoreAddonState) {
      const flags = enhancementFlags(variant.options[enhancementIndex]);
      addonInputs.forEach((input) => { input.checked = Boolean(flags[input.dataset.customAddon]); });
    }
    masterVariant.value = variant.id;
    return true;
  }

  function preferredVariantForSize(sizeKey) {
    const flags = addonFlags();
    const selectedDrill = optionInputs.find((input) => Number(input.dataset.optionIndex) === drillIndex && input.checked)?.value || 'Round';
    const candidates = variants.filter((variant) => {
      const enhancement = enhancementFlags(variant.options[enhancementIndex]);
      return variant.available &&
        variant.options.some((value) => parseSize(value)?.key === sizeKey) &&
        variant.options[drillIndex] === selectedDrill &&
        enhancement.ab === flags.ab &&
        enhancement.priority === flags.priority;
    });
    return candidates.sort((first, second) => {
      const score = (variant) => variant.options.reduce((totalScore, value) => {
        if (/^round$/i.test(value)) return totalScore + 2;
        return totalScore;
      }, 0);
      return score(second) - score(first);
    })[0] || null;
  }

  function selectRecommendedSize() {
    if (!selectedImage || userSelectedSize) return false;
    const recommendation = preferredSizeRecommendation();
    const source = sourceDimensions();
    const available = sizePicker?.ordered || [];
    if (!source || !available.length) return false;
    const sourceRatio = Math.max(source.width / source.height, source.height / source.width);
    const closestDifference = Math.min(...available.map((entry) => ratioDifference(entry.size.ratio, sourceRatio)));
    const compatible = available.filter((entry) => Math.abs(ratioDifference(entry.size.ratio, sourceRatio) - closestDifference) < 0.001);
    let selected = null;
    recommendation?.keys.some((key) => {
      selected = compatible.find((entry) => entry.size?.key === key && preferredVariantForSize(key));
      return Boolean(selected);
    });
    if (!selected) {
      selected = [...compatible].sort((first, second) => {
        const firstMeets = !recommendation?.minimumArea || first.size.area >= recommendation.minimumArea;
        const secondMeets = !recommendation?.minimumArea || second.size.area >= recommendation.minimumArea;
        if (firstMeets !== secondMeets) return firstMeets ? -1 : 1;
        return first.size.area - second.size.area;
      })[0];
    }
    return Boolean(selected && applyVariantSelection(preferredVariantForSize(selected.size.key)));
  }

  function refreshSizePicker() {
    if (!sizePicker) return;
    const { ordered, toggle } = sizePicker;
    const source = sourceDimensions();
    const sourceRatio = source ? Math.max(source.width / source.height, source.height / source.width) : 0;
    let closestRatio = null;
    if (sourceRatio) {
      closestRatio = ordered.reduce((best, entry) => {
        const difference = ratioDifference(sourceRatio, entry.size.ratio);
        return !best || difference < best.difference ? { ratio: entry.size.ratio, difference } : best;
      }, null)?.ratio;
    }
    const recommended = source && !showAllSizes
      ? ordered.filter((entry) => Math.abs(entry.size.ratio - closestRatio) < 0.001)
      : ordered;
    const selectedEntry = ordered.find((entry) => entry.choice.querySelector('input:checked'));
    const visibleEntries = source ? recommended : [];
    const visible = new Set(visibleEntries.map((entry) => entry.choice));
    if (selectedEntry) visible.add(selectedEntry.choice);
    ordered.forEach((entry) => {
      entry.choice.hidden = !visible.has(entry.choice);
      entry.choice.classList.toggle('is-extra-size', !visible.has(entry.choice));
    });
    const canToggle = Boolean(source && recommended.length < ordered.length);
    toggle.hidden = !canToggle;
    toggle.setAttribute('aria-expanded', String(showAllSizes));
    toggle.innerHTML = `<small>Advanced</small><strong>Custom Size</strong><span>${showAllSizes ? 'Show recommended sizes' : 'Choose any available size'}</span>`;
  }

  function prepareSizePicker() {
    const group = optionGroups.find((optionGroup) => optionGroup.dataset.optionKind === 'size');
    const grid = group?.querySelector('.pd-custom-option__cards');
    const toggle = group?.querySelector('[data-size-toggle]');
    if (!grid || !toggle) return;

    const choices = [...grid.querySelectorAll('[data-option-choice]')];
    const ordered = choices.map((choice, originalIndex) => {
      const value = choice.querySelector('[data-custom-option]')?.value || '';
      const size = parseSize(value);
      return { choice, originalIndex, size: size || { area: Number.POSITIVE_INFINITY, width: 0, height: 0, ratio: 1, key: '' } };
    }).sort((first, second) => first.size.area - second.size.area || Math.max(first.size.width, first.size.height) - Math.max(second.size.width, second.size.height) || first.originalIndex - second.originalIndex);

    ordered.forEach(({ choice }) => grid.append(choice));
    grid.append(toggle);
    sizePicker = { group, grid, toggle, ordered };
    toggle.addEventListener('click', () => {
      showAllSizes = !showAllSizes;
      refreshSizePicker();
    });
    refreshSizePicker();
  }

  function renderVariant() {
    const selections = selectedValues();
    const variant = findVariant(selections);
    const configured = Boolean(optionsAreAvailable() && variant);
    if (optionsAreAvailable() && selections.filter(Boolean).length === product.optionsCount && !variant) {
      console.warn('[Painting Diamonds custom builder] No Shopify variant matches the customer selection.', {
        selections,
        addons: addonFlags()
      });
    }
    if (configured) masterVariant.value = variant.id;
    if (price) price.textContent = configured ? formatMoney(variant.price) : '';
    if (total) total.hidden = !configured;
    if (comparePrice) {
      const onSale = Boolean(configured && variant.compareAtPrice > variant.price);
      comparePrice.hidden = !onSale;
      comparePrice.textContent = onSale ? formatMoney(variant.compareAtPrice) : '';
    }
    if (optionsAreAvailable()) {
      updateOptionAvailability(selections);
      updateAddonControls();
    } else {
      optionInputs.forEach((input) => { input.disabled = true; });
      addonInputs.forEach((input) => { input.disabled = true; });
    }
    updateSubmitState(configured ? variant : null);
    refreshSizePicker();
    if (selectedImage) analyseImage();
    schedulePreview();
    scheduleProductionExport();

    if (configured) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  function updateSubmitState(variant = currentVariant()) {
    const configured = Boolean(optionsAreAvailable() && variant?.available);
    const signature = configured ? productionSignature() : '';
    const finalImageIsCurrent = Boolean(uploadedUrl && signature && uploadedProductionSignature === signature);
    const ready = Boolean(finalImageIsCurrent && configured && !uploadInProgress && !replacementAdded);
    const showButtonPrice = Boolean(configured && !replacementAdded);
    if (replacementAdded) setBuilderState(STATE.RESULT);
    else if (!selectedImage) setBuilderState(STATE.WAITING);
    else if (uploadInProgress) setBuilderState(STATE.BUSY);
    else if (ready) setBuilderState(STATE.READY);
    else if (builderState !== STATE.RESULT) setBuilderState(STATE.OPTIONS);
    submit.disabled = !ready;
    submit.removeAttribute('aria-busy');
    submitPrice.hidden = !showButtonPrice;
    submitPrice.textContent = showButtonPrice ? formatMoney(variant.price) : '';
    if (replacementAdded) {
      submitLabel.textContent = 'Updated kit added';
      submitNote.textContent = 'Review the updated item in your cart.';
    } else if (uploadInProgress) {
      submitLabel.textContent = 'Preparing your custom photo…';
      submitNote.textContent = 'Please wait while your photo is securely prepared.';
    } else if (!selectedImage) {
      submitLabel.textContent = 'Add to cart';
      submitNote.textContent = 'Upload your photo before adding to cart.';
    } else if (!variant) {
      submitLabel.textContent = 'Combination unavailable';
      submitNote.textContent = 'Choose another combination of options.';
    } else if (!variant.available) {
      submitLabel.textContent = 'Selected kit is sold out';
      submitNote.textContent = 'Choose another available option.';
    } else if (!finalImageIsCurrent) {
      submitLabel.textContent = 'Add to cart';
      submitNote.textContent = 'Please wait while your final crop is securely prepared.';
    } else {
      submitLabel.textContent = 'Add to cart';
      submitNote.textContent = 'Your persistent uploaded photo will be attached to this custom kit.';
    }
  }

  function targetGrid() {
    const variant = currentVariant();
    const size = variant?.options.map(parseSize).find(Boolean) || parseSize('40x40');
    const source = sourceDimensions();
    const landscape = source && source.width >= source.height;
    const width = landscape ? Math.max(size.width, size.height) : Math.min(size.width, size.height);
    const height = landscape ? Math.min(size.width, size.height) : Math.max(size.width, size.height);
    return {
      ratio: width / height,
      columns: Math.round(width * DRILLS_PER_CM),
      rows: Math.round(height * DRILLS_PER_CM),
      widthCm: width,
      heightCm: height,
      size
    };
  }

  function makeSourceCanvas() {
    const rotated = Math.abs(rotation % 180) === 90;
    const source = document.createElement('canvas');
    source.width = rotated ? selectedImage.naturalHeight : selectedImage.naturalWidth;
    source.height = rotated ? selectedImage.naturalWidth : selectedImage.naturalHeight;
    const sourceContext = source.getContext('2d');
    sourceContext.filter = `brightness(${brightness}%)`;
    sourceContext.translate(source.width / 2, source.height / 2);
    sourceContext.rotate(rotation * Math.PI / 180);
    sourceContext.drawImage(selectedImage, -selectedImage.naturalWidth / 2, -selectedImage.naturalHeight / 2);
    return source;
  }

  function cropGeometry(source, targetRatio) {
    const sourceRatio = source.width / source.height;
    let cropWidth;
    let cropHeight;
    if (sourceRatio > targetRatio) {
      cropHeight = source.height / zoom;
      cropWidth = cropHeight * targetRatio;
    } else {
      cropWidth = source.width / zoom;
      cropHeight = cropWidth / targetRatio;
    }
    cropWidth = Math.min(cropWidth, source.width);
    cropHeight = Math.min(cropHeight, source.height);
    const maxPanX = source.width - cropWidth;
    const maxPanY = source.height - cropHeight;
    return {
      x: (maxPanX / 2) + (panX * maxPanX / 2),
      y: (maxPanY / 2) + (panY * maxPanY / 2),
      width: cropWidth,
      height: cropHeight
    };
  }

  function drawCropped(targetContext, targetWidth, targetHeight) {
    const source = makeSourceCanvas();
    const crop = cropGeometry(source, targetWidth / targetHeight);
    targetContext.drawImage(source, crop.x, crop.y, crop.width, crop.height, 0, 0, targetWidth, targetHeight);
  }

  function productionSignature() {
    if (!selectedImage) return '';
    const variant = currentVariant();
    const size = variant?.options.map(parseSize).find(Boolean);
    if (!size) return '';
    return JSON.stringify({
      source: kitInstance,
      size: size.key,
      zoom: Number(zoom).toFixed(3),
      panX: Number(panX).toFixed(4),
      panY: Number(panY).toFixed(4),
      rotation: ((rotation % 360) + 360) % 360,
      brightness: Number(brightness)
    });
  }

  function createProductionCanvas() {
    const grid = targetGrid();
    const source = makeSourceCanvas();
    const crop = cropGeometry(source, grid.ratio);
    const maximumDimension = 8192;
    const maximumPixels = 24000000;
    const scale = Math.min(
      1,
      maximumDimension / crop.width,
      maximumDimension / crop.height,
      Math.sqrt(maximumPixels / (crop.width * crop.height))
    );
    let outputWidth = Math.max(1, Math.floor(crop.width * scale));
    let outputHeight = Math.max(1, Math.round(outputWidth / grid.ratio));
    const availableHeight = Math.max(1, Math.floor(crop.height * scale));
    if (outputHeight > availableHeight) {
      outputHeight = availableHeight;
      outputWidth = Math.max(1, Math.round(outputHeight * grid.ratio));
    }
    const output = document.createElement('canvas');
    output.width = outputWidth;
    output.height = outputHeight;
    const outputContext = output.getContext('2d');
    outputContext.fillStyle = '#fff';
    outputContext.fillRect(0, 0, outputWidth, outputHeight);
    outputContext.imageSmoothingEnabled = true;
    outputContext.imageSmoothingQuality = 'high';
    outputContext.drawImage(source, crop.x, crop.y, crop.width, crop.height, 0, 0, outputWidth, outputHeight);
    return output;
  }

  function canvasBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('The final crop could not be generated. Please try again.'));
      }, 'image/jpeg', 0.95);
    });
  }

  function maxColoursForSize(size) {
    return Math.min(size.width, size.height) <= 30 && Math.max(size.width, size.height) <= 40
      ? SMALL_PAINTING_MAX_COLOURS
      : LARGE_PAINTING_MAX_COLOURS;
  }

  function limitCanvasColours(target, maximum) {
    const targetContext = target.getContext('2d');
    const imageData = targetContext.getImageData(0, 0, target.width, target.height);
    const data = imageData.data;
    const buckets = {};
    for (let index = 0; index < data.length; index += 4) {
      const key = `${data[index] >> 2},${data[index + 1] >> 2},${data[index + 2] >> 2}`;
      const bucket = buckets[key] || (buckets[key] = { count: 0, r: 0, g: 0, b: 0 });
      bucket.count += 1;
      bucket.r += data[index];
      bucket.g += data[index + 1];
      bucket.b += data[index + 2];
    }
    const points = Object.values(buckets).map((bucket) => ({
      count: bucket.count,
      r: Math.round(bucket.r / bucket.count),
      g: Math.round(bucket.g / bucket.count),
      b: Math.round(bucket.b / bucket.count)
    }));
    const makeBox = (boxPoints) => {
      const box = { points: boxPoints, count: 0, rMin: 255, rMax: 0, gMin: 255, gMax: 0, bMin: 255, bMax: 0 };
      boxPoints.forEach((point) => {
        box.count += point.count;
        box.rMin = Math.min(box.rMin, point.r);
        box.rMax = Math.max(box.rMax, point.r);
        box.gMin = Math.min(box.gMin, point.g);
        box.gMax = Math.max(box.gMax, point.g);
        box.bMin = Math.min(box.bMin, point.b);
        box.bMax = Math.max(box.bMax, point.b);
      });
      box.range = boxPoints.length ? Math.max(box.rMax - box.rMin, box.gMax - box.gMin, box.bMax - box.bMin) : 0;
      return box;
    };
    const boxes = [makeBox(points)];
    while (boxes.length < maximum) {
      boxes.sort((first, second) => (second.range * second.count) - (first.range * first.count));
      const box = boxes.shift();
      if (!box || box.points.length < 2 || !box.range) {
        if (box) boxes.push(box);
        break;
      }
      const ranges = { r: box.rMax - box.rMin, g: box.gMax - box.gMin, b: box.bMax - box.bMin };
      const channel = ranges.g >= ranges.r && ranges.g >= ranges.b ? 'g' : (ranges.b >= ranges.r && ranges.b >= ranges.g ? 'b' : 'r');
      box.points.sort((first, second) => first[channel] - second[channel]);
      let running = 0;
      let split = 1;
      for (let index = 0; index < box.points.length; index += 1) {
        running += box.points[index].count;
        if (running >= box.count / 2) {
          split = Math.max(1, index + 1);
          break;
        }
      }
      if (split >= box.points.length) split = Math.max(1, Math.floor(box.points.length / 2));
      boxes.push(makeBox(box.points.slice(0, split)), makeBox(box.points.slice(split)));
    }
    const palette = boxes.map((box) => {
      const totals = box.points.reduce((sum, point) => ({
        count: sum.count + point.count,
        r: sum.r + point.r * point.count,
        g: sum.g + point.g * point.count,
        b: sum.b + point.b * point.count
      }), { count: 0, r: 0, g: 0, b: 0 });
      return totals.count ? { r: Math.round(totals.r / totals.count), g: Math.round(totals.g / totals.count), b: Math.round(totals.b / totals.count) } : null;
    }).filter(Boolean);
    const cache = {};
    for (let index = 0; index < data.length; index += 4) {
      const key = `${data[index] >> 2},${data[index + 1] >> 2},${data[index + 2] >> 2}`;
      let colour = cache[key];
      if (!colour) {
        colour = palette.reduce((best, candidate) => {
          const distance = ((data[index] - candidate.r) ** 2) + ((data[index + 1] - candidate.g) ** 2) + ((data[index + 2] - candidate.b) ** 2);
          return !best || distance < best.distance ? { ...candidate, distance } : best;
        }, null);
        cache[key] = colour;
      }
      data[index] = colour.r;
      data[index + 1] = colour.g;
      data[index + 2] = colour.b;
    }
    targetContext.putImageData(imageData, 0, 0);
  }

  function drawPreview() {
    if (!selectedImage) return;
    const grid = targetGrid();
    const cellSize = Math.max(2, Math.min(6, Math.floor(Math.min(900 / grid.columns, 1200 / grid.rows))));
    canvas.width = grid.columns * cellSize;
    canvas.height = grid.rows * cellSize;
    context.clearRect(0, 0, canvas.width, canvas.height);

    if (previewMode === 'original') {
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      drawCropped(context, canvas.width, canvas.height);
      return;
    }

    const pixels = document.createElement('canvas');
    pixels.width = grid.columns;
    pixels.height = grid.rows;
    const pixelsContext = pixels.getContext('2d');
    pixelsContext.imageSmoothingEnabled = true;
    pixelsContext.imageSmoothingQuality = 'high';
    drawCropped(pixelsContext, pixels.width, pixels.height);
    limitCanvasColours(pixels, maxColoursForSize(grid.size));
    context.imageSmoothingEnabled = false;
    context.drawImage(pixels, 0, 0, canvas.width, canvas.height);
  }

  function schedulePreview() {
    window.cancelAnimationFrame(previewFrame);
    previewFrame = window.requestAnimationFrame(drawPreview);
  }

  function resetEditor() {
    zoom = 1;
    rotation = 0;
    brightness = 100;
    panX = 0;
    panY = 0;
    zoomControl.value = '1';
    brightnessControl.value = '100';
    schedulePreview();
    invalidateProductionImage();
  }

  function analyseImage() {
    if (!selectedImage) return;
    const grid = targetGrid();
    const selectedSize = grid.size;
    const source = sourceDimensions();
    const sourceRatio = Math.max(source.width / source.height, source.height / source.width);
    const available = sizePicker?.ordered || [];
    const closest = available.reduce((best, entry) => {
      const difference = ratioDifference(sourceRatio, entry.size.ratio);
      return !best || difference < best.difference ? { ...entry, difference } : best;
    }, null);
    const selectedDifference = ratioDifference(sourceRatio, selectedSize.ratio);
    const recommendation = preferredSizeRecommendation();
    const recommendedMinimum = available
      .filter((entry) => closest && Math.abs(entry.size.ratio - closest.size.ratio) < 0.001 && entry.size.area >= (recommendation?.minimumArea || 0))
      .sort((first, second) => first.size.area - second.size.area)[0];
    const lowerDetail = Boolean(recommendedMinimum && selectedSize.area < recommendedMinimum.size.area);
    const cropSuggestion = Boolean(closest && closest.difference + 0.025 < selectedDifference && closest.size.key !== selectedSize.key);
    analysis.classList.toggle('is-warning', lowerDetail || cropSuggestion);
    analysis.replaceChildren();
    const heading = document.createElement('strong');
    heading.textContent = lowerDetail ? 'MAY LOSE DETAIL AT THIS SIZE' : 'PHOTO READY';
    const details = document.createElement('span');
    details.textContent = `${selectedImage.naturalWidth} × ${selectedImage.naturalHeight}px source. Preview: ${grid.columns} × ${grid.rows} diamonds (${grid.widthCm} × ${grid.heightCm}cm).`;
    analysis.append(heading, details);
    if (lowerDetail) {
      const warning = document.createElement('span');
      warning.textContent = `For best results, choose at least ${recommendedMinimum.size.label} for this photo shape.`;
      analysis.append(warning);
    }
    if (cropSuggestion) {
      const suggestion = document.createElement('span');
      suggestion.textContent = `${closest.size.label} is closer to the photo ratio and should crop less. `;
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.applySizeSuggestion = closest.size.key;
      button.textContent = 'Use this size';
      suggestion.append(button);
      analysis.append(suggestion);
    }
  }

  function customKitId() {
    return `KIT-${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
  }

  let kitInstance = customKitId();
  kitInstanceInput.value = kitInstance;

  function setUploadMessage(message, isError = false) {
    uploadStatus.textContent = message;
    uploadStatus.classList.toggle('is-error', isError);
  }

  function persistentPhotoUrl(data) {
    const nested = data.file || data.photo || data.upload || data.image || {};
    const value = data.uploadedPhotoUrl || data.url || data.publicUrl || data.imageUrl || nested.uploadedPhotoUrl || nested.url || nested.publicUrl || nested.imageUrl || '';
    try {
      const url = new URL(value);
      return /^https?:$/.test(url.protocol) ? url.href : '';
    } catch (error) {
      return '';
    }
  }

  async function uploadProductionImage(signature, revision) {
    uploadController?.abort();
    uploadController = new AbortController();
    uploadInProgress = true;
    uploadedUrl = '';
    uploadedProductionSignature = '';
    photoUrlInput.value = '';
    retry.hidden = true;
    setUploadMessage('Preparing your final crop…');
    updateSubmitState();

    try {
      const output = createProductionCanvas();
      const blob = await canvasBlob(output);
      if (revision !== productionExportRevision || signature !== productionSignature()) return;
      const file = new File([blob], `${kitInstance}-final.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
      const body = new FormData();
      body.append('file', file);
      body.append('customKitId', kitInstance);
      const response = await fetch(customRoot.dataset.uploadEndpoint, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { Accept: 'application/json' },
        body,
        signal: uploadController.signal
      });
      const responseText = await response.text();
      let data = {};
      try { data = JSON.parse(responseText); } catch (error) { data = { message: responseText }; }
      if (!response.ok) throw new Error(data.error || data.message || 'Photo upload failed. Please try again.');
      const finalUrl = persistentPhotoUrl(data);
      if (!finalUrl) throw new Error('The upload completed without a persistent photo URL. Please try again.');
      if (revision !== productionExportRevision || signature !== productionSignature()) return;
      uploadedUrl = finalUrl;
      uploadedProductionSignature = signature;
      pendingProductionSignature = '';
      photoUrlInput.value = uploadedUrl;
      customRoot.classList.add('has-photo');
      setUploadMessage('Final crop ready for your custom kit.');
      optionsGuidance.textContent = 'Your photo is ready. Choose your size, diamond type and optional extras.';
      optionsGuidance.classList.add('is-ready');
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (revision !== productionExportRevision) return;
      uploadedUrl = '';
      uploadedProductionSignature = '';
      pendingProductionSignature = '';
      photoUrlInput.value = '';
      retry.hidden = false;
      setUploadMessage(error.message, true);
      setBuilderState(STATE.RESULT);
    } finally {
      if (revision === productionExportRevision) {
        uploadInProgress = false;
        updateSubmitState();
      }
    }
  }

  function scheduleProductionExport(delay = 650) {
    const signature = productionSignature();
    if (!signature || !optionsAreAvailable()) return;
    if (uploadedUrl && uploadedProductionSignature === signature) return;
    if (pendingProductionSignature === signature && (productionExportTimer || uploadInProgress)) return;

    productionExportRevision += 1;
    const revision = productionExportRevision;
    pendingProductionSignature = signature;
    uploadedUrl = '';
    uploadedProductionSignature = '';
    photoUrlInput.value = '';
    uploadController?.abort();
    uploadInProgress = false;
    window.clearTimeout(productionExportTimer);
    productionExportTimer = window.setTimeout(() => {
      productionExportTimer = 0;
      uploadProductionImage(signature, revision);
    }, delay);
    setUploadMessage('Updating your final crop…');
    updateSubmitState();
  }

  function invalidateProductionImage(delay = 650) {
    pendingProductionSignature = '';
    scheduleProductionExport(delay);
  }

  function validImage(file) {
    return Boolean(file && (/^image\/(jpeg|png|webp)$/i.test(file.type) || /\.(jpe?g|png|webp)$/i.test(file.name)));
  }

  function loadPhoto(file) {
    if (!validImage(file)) {
      setUploadMessage('Please choose a JPEG, PNG or WebP image.', true);
      return;
    }

    kitInstance = customKitId();
    kitInstanceInput.value = kitInstance;
    const objectUrl = URL.createObjectURL(file);
    const nextImage = new Image();
    nextImage.onload = () => {
      selectedImage = nextImage;
      userSelectedSize = false;
      showAllSizes = false;
      setBuilderState(STATE.EDITING);
      resetEditor();
      selectRecommendedSize();
      setBuilderState(STATE.OPTIONS);
      refreshSizePicker();
      dropzone.hidden = true;
      editor.hidden = false;
      customRoot.classList.add('has-photo');
      URL.revokeObjectURL(objectUrl);
      renderVariant();
    };
    nextImage.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setUploadMessage('This image could not be opened. Please choose another file.', true);
    };
    nextImage.src = objectUrl;
  }

  function removePhoto() {
    uploadController?.abort();
    window.clearTimeout(productionExportTimer);
    productionExportTimer = 0;
    productionExportRevision += 1;
    pendingProductionSignature = '';
    uploadedProductionSignature = '';
    selectedImage = null;
    uploadedUrl = '';
    uploadInProgress = false;
    photoUrlInput.value = '';
    fileInput.value = '';
    editor.hidden = true;
    dropzone.hidden = false;
    retry.hidden = true;
    optionsGuidance.textContent = 'Upload your photo to choose your size and options.';
    optionsGuidance.classList.remove('is-ready');
    customRoot.classList.remove('has-photo');
    userSelectedSize = false;
    showAllSizes = false;
    addonInputs.forEach((input) => { input.checked = false; });
    optionInputs.filter((input) => Number(input.dataset.optionIndex) === drillIndex).forEach((input) => { input.checked = false; });
    refreshSizePicker();
    setUploadMessage('');
    updateSubmitState();
  }

  fileInput.addEventListener('change', () => loadPhoto(fileInput.files[0]));
  dropzone.addEventListener('dragover', (event) => { event.preventDefault(); dropzone.classList.add('is-dragging'); });
  dropzone.addEventListener('dragleave', () => dropzone.classList.remove('is-dragging'));
  dropzone.addEventListener('drop', (event) => {
    event.preventDefault();
    dropzone.classList.remove('is-dragging');
    loadPhoto(event.dataTransfer.files[0]);
  });
  retry.addEventListener('click', () => {
    pendingProductionSignature = '';
    scheduleProductionExport(0);
  });
  query('[data-change-photo]').addEventListener('click', () => fileInput.click());
  query('[data-remove-photo]').addEventListener('click', removePhoto);
  query('[data-reset-photo]').addEventListener('click', resetEditor);
  query('[data-rotate-left]').addEventListener('click', () => {
    rotation = (rotation - 90) % 360;
    panX = 0;
    panY = 0;
    refreshSizePicker();
    analyseImage();
    schedulePreview();
    invalidateProductionImage();
  });
  query('[data-rotate-right]').addEventListener('click', () => {
    rotation = (rotation + 90) % 360;
    panX = 0;
    panY = 0;
    refreshSizePicker();
    analyseImage();
    schedulePreview();
    invalidateProductionImage();
  });
  zoomControl.addEventListener('input', (event) => { zoom = Number(event.target.value); schedulePreview(); invalidateProductionImage(); });
  brightnessControl.addEventListener('input', (event) => { brightness = Number(event.target.value); schedulePreview(); invalidateProductionImage(); });
  previewModeButtons.forEach((button) => button.addEventListener('click', () => {
    previewMode = button.dataset.previewMode;
    previewModeButtons.forEach((control) => control.setAttribute('aria-pressed', String(control === button)));
    schedulePreview();
  }));

  canvas.addEventListener('pointerdown', (event) => {
    dragging = true;
    dragStart = { x: event.clientX, y: event.clientY, panX, panY };
    canvas.setPointerCapture(event.pointerId);
  });
  canvas.addEventListener('pointermove', (event) => {
    if (!dragging) return;
    const bounds = canvas.getBoundingClientRect();
    panX = Math.max(-1, Math.min(1, dragStart.panX - (event.clientX - dragStart.x) / bounds.width * 2));
    panY = Math.max(-1, Math.min(1, dragStart.panY - (event.clientY - dragStart.y) / bounds.height * 2));
    schedulePreview();
    invalidateProductionImage();
  });
  canvas.addEventListener('pointerup', () => { dragging = false; });
  canvas.addEventListener('pointercancel', () => { dragging = false; });
  canvas.addEventListener('keydown', (event) => {
    const movement = { ArrowLeft: [-0.05, 0], ArrowRight: [0.05, 0], ArrowUp: [0, -0.05], ArrowDown: [0, 0.05] }[event.key];
    if (!movement) return;
    event.preventDefault();
    panX = Math.max(-1, Math.min(1, panX + movement[0]));
    panY = Math.max(-1, Math.min(1, panY + movement[1]));
    schedulePreview();
    invalidateProductionImage();
  });

  prepareOptionLayout();
  prepareSizePicker();
  optionInputs.forEach((input) => input.addEventListener('change', () => {
    const group = input.closest('[data-custom-option-group]');
    if (group?.dataset.optionKind === 'size') userSelectedSize = true;
    renderVariant();
  }));
  addonInputs.forEach((input) => input.addEventListener('change', renderVariant));

  analysis.addEventListener('click', (event) => {
    const button = event.target.closest('[data-apply-size-suggestion]');
    if (!button) return;
    const input = matchingSizeInput(button.dataset.applySizeSuggestion);
    if (!input) return;
    userSelectedSize = true;
    input.checked = true;
    renderVariant();
  });

  queryAll('.pd-custom-faq details').forEach((details) => details.addEventListener('toggle', () => {
    if (!details.open) return;
    queryAll('.pd-custom-faq details').forEach((other) => {
      if (other !== details) other.open = false;
    });
  }));

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const variant = currentVariant();
    if (replacementAdded) return;
    if (uploadInProgress) {
      formStatus.textContent = 'Please wait while your photo finishes uploading.';
      return;
    }
    if (!uploadedUrl || photoUrlInput.value !== uploadedUrl) {
      formStatus.textContent = 'Upload your photo successfully before adding this custom kit.';
      return;
    }
    if (!uploadedProductionSignature || uploadedProductionSignature !== productionSignature()) {
      formStatus.textContent = 'Please wait while the latest crop finishes preparing.';
      scheduleProductionExport(0);
      return;
    }
    if (!variant?.available) {
      formStatus.textContent = 'Choose an available combination before adding this kit.';
      return;
    }

    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');
    setBuilderState(STATE.BUSY);
    submitLabel.textContent = editKey ? 'Updating custom kit…' : 'Adding custom kit…';
    submitPrice.hidden = true;
    formStatus.textContent = '';

    try {
      const addResponse = await fetch(window.PDTheme.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const addedItem = await addResponse.json();
      if (!addResponse.ok) throw new Error(addedItem.description || 'Could not add this custom kit.');
      replacementAdded = true;

      let removalWarning = '';
      if (editKey) {
        try {
          const cartResponse = await fetch(`${window.PDTheme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
          if (!cartResponse.ok) throw new Error('Current cart could not be loaded.');
          const currentCart = await cartResponse.json();
          const originalLine = currentCart.items.find((item) => item.key === editKey && item.key !== addedItem.key)
            || currentCart.items.find((item) => item.key !== addedItem.key
              && item.properties?.['Uploaded Photo URL'] === existingPhotoUrl
              && (!originalVariantId || String(item.variant_id) === String(originalVariantId)));
          if (!originalLine) throw new Error('Original cart line could not be identified safely.');
          const removal = await fetch(window.PDTheme.routes.cartChange, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: originalLine.key, quantity: 0 })
          });
          if (!removal.ok) throw new Error('Original cart line was not removed.');
        } catch (removalError) {
          removalWarning = 'The updated kit was added, but the original item could not be removed. Please remove the original from your cart.';
        }
      }

      try {
        await window.PDCart.refresh(true);
      } catch (cartError) {
        removalWarning = removalWarning || 'Your custom kit was added, but the cart preview could not refresh. Open your cart to review it.';
      }
      success.hidden = false;
      setBuilderState(STATE.RESULT);
      successMessage.textContent = removalWarning || 'Your persistent uploaded photo and selected kit options are attached.';
      form.hidden = true;
      if (removalWarning) formStatus.textContent = removalWarning;
    } catch (error) {
      formStatus.textContent = error.message;
      submit.removeAttribute('aria-busy');
      setBuilderState(STATE.RESULT);
      updateSubmitState();
    }
  });

  query('[data-custom-view-cart]').addEventListener('click', () => window.PDCart.refresh(true));

  setBuilderState(isEditSession ? STATE.EDITING : STATE.WAITING);
  if (isEditSession) {
    const existingPhoto = editPhoto;
    existingPhotoUrl = existingPhoto || '';
    editKey = parameters.get('dp_line') || parameters.get('dp_key') || '';
    const variantId = parameters.get('variant');
    originalVariantId = variantId || '';
    const editVariant = variants.find((variant) => String(variant.id) === String(variantId));
    if (existingPhoto) {
      const restoredImage = new Image();
      restoredImage.crossOrigin = 'anonymous';
      restoredImage.onload = () => {
        selectedImage = restoredImage;
        userSelectedSize = true;
        dropzone.hidden = true;
        editor.hidden = false;
        setUploadMessage('Editing your existing custom kit.');
        optionsGuidance.textContent = 'Your existing photo is ready. Review the options before updating the cart.';
        optionsGuidance.classList.add('is-ready');
        customRoot.classList.add('has-photo');
        setBuilderState(STATE.EDITING);
        if (editVariant) applyVariantSelection(editVariant, true);
        else selectRecommendedSize();
        resetEditor();
        setBuilderState(STATE.OPTIONS);
        uploadedUrl = existingPhoto;
        photoUrlInput.value = existingPhoto;
        uploadedProductionSignature = productionSignature();
        pendingProductionSignature = '';
        renderVariant();
      };
      restoredImage.onerror = () => setUploadMessage('The existing cart photo could not be loaded. Choose a replacement before updating.', true);
      restoredImage.src = existingPhoto;
    }
  }

  renderVariant();
}
